import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Linkedin, Instagram, TrendingUp, Truck, CreditCard, Handshake, ArrowRight } from 'lucide-react';
import { CONFIG } from '../config';
import { useModal } from '../context/ModalContext';

const FooterExact = () => {
  const { openModal } = useModal();

  return (
    <footer className="bg-white text-slate-900">
      {/* CTA Panel */}
      <div className="bg-[#5C1F33] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 xl:grid-cols-[1.8fr_1fr] gap-6 items-center">
            <div>
              <p className="text-sm text-white uppercase tracking-[0.24em] mb-3">Need Funding to Fuel Your Business Growth?</p>
              <h2 className="text-3xl sm:text-4xl font-semibold leading-tight text-white max-w-3xl bg-gradient-to-r from-[#5C1F33]/15 via-transparent to-[#5C1F33]/10 p-6 rounded-3xl border border-white/10">
                Unlock working capital with customised finance for every growth stage
              </h2>
              <p className="mt-4 text-sm text-white/70 max-w-2xl">
                with quick assessment and fast approval.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openModal();
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#5C1F33] hover:bg-slate-100 transition"
                >
                  Check Your Eligibility
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:border-white/40 hover:bg-white/10 transition"
                >
                  Talk to Funding Expert
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-2">
              <div className="rounded-3xl bg-white/5 p-5 border border-white/10">
                <p className="text-base font-semibold text-white">Quick Turnaround</p>
                <p className="mt-3 text-sm text-white/70">Fast</p>
              </div>
              <div className="rounded-3xl bg-white/5 p-5 border border-white/10">
                <p className="text-base font-semibold text-white">Minimal Documentation</p>
                <p className="mt-3 text-sm text-white/70">Simple onboarding</p>
              </div>
              <div className="rounded-3xl bg-white/5 p-5 border border-white/10 col-span-2 sm:col-span-1 xl:col-span-2">
                <p className="text-base font-semibold text-white">For MSMEs, Traders & Enterprises</p>
                <p className="mt-3 text-sm text-white/70">Flexible finance options tailored for your business size.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 bg-white text-slate-900">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-6 mb-10 bg-white">
          <div className="flex items-center gap-3">
            <img
              src="/company-logo.png"
              alt="Funding Sathi Logo"
              className="h-14 w-auto"
            />
            <span className="text-3xl font-semibold text-white">Funding Sathi</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={CONFIG.LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/20 bg-[#0A66C2] flex items-center justify-center text-white hover:bg-[#084a8a] transition"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href={CONFIG.INSTAGRAM}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/20 bg-gradient-to-br from-[#f58529] via-[#c13584] to-[#833ab4] flex items-center justify-center text-white hover:opacity-90 transition"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="border-t border-slate-200 mb-10" />

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-10">
          <div>
            <h4 className="font-semibold text-sm mb-5 text-slate-900 tracking-wide">Funding Solutions</h4>
            <div className="space-y-4 text-sm text-slate-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <span>Working Capital Solutions</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <Truck className="w-4 h-4 text-white" />
                </div>
                <span>Supply Chain Finance</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
                <span>Invoice Discounting</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <Handshake className="w-4 h-4 text-white" />
                </div>
                <span>Vendor Finance</span>
              </div>
            </div>
            <Link to="/services" className="mt-5 ml-3 inline-flex items-center gap-2 text-sm text-slate-900 hover:text-slate-700 transition">
              View All Solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-5 text-slate-900 tracking-wide">Legal</h4>
            <ul className="space-y-3 text-sm text-slate-600">
              <li><Link to="/terms" className="hover:text-slate-900 transition">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-slate-900 transition">Privacy Policy</Link></li>
              <li><Link to="/risk-disclosure" className="hover:text-slate-900 transition">Risk Disclosure</Link></li>
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-1">
            <h4 className="font-semibold text-sm mb-5 text-slate-900 tracking-wide">Get in Touch</h4>
            <ul className="space-y-4 text-sm text-slate-600">
              <li className="flex gap-3 items-start border-b border-slate-200 pb-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>Corporate Office, Lake City, Kapurbawadi Junction, Thane West – 400607, Maharashtra</div>
              </li>
              <li className="flex gap-3 items-center border-b border-slate-200 pb-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div><a href="tel:+912248152124" className="text-slate-700 hover:text-slate-900 transition">+91 22 4815 2124</a></div>
              </li>
              <li className="flex gap-3 items-center border-b border-slate-200 pb-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div><a href="mailto:info@fundingsathi.in" className="text-slate-700 hover:text-slate-900 transition">info@fundingsathi.in</a></div>
              </li>
              <li className="flex gap-3 items-center">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#5C1F33] flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
                <div>Mon – Fri | 9:30 AM – 6:30 PM</div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3 text-sm text-slate-500">
              <p>© 2026 Funding Sathi. All Rights Reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterExact;
